import { useState, useEffect } from 'react';
import { ArrowLeft, LogOut, Award, Wine } from 'lucide-react';
import nhost from '../nhost';
import { avg, sweDate } from '../helpers';
import { awardPoints } from '../points';
import { checkBadges } from '../badges';
import ImageUpload from './ImageUpload';
import PointsBreakdown from './PointsBreakdown';
import usePushSubscription from '../usePushSubscription';

const PROFILE_QUERY = `
  query GetProfileStats($userId: uuid!) {
    profiles_by_pk(id: $userId) { phone }
    # All ratings by this user
    ratings(where: { user_id: { _eq: $userId } }, order_by: { created_at: desc }) {
      id rating comment created_at
      wine {
        id name vintage region type grape
        session { id completed event { event_date } profile { display_name } }
      }
    }
    # Sessions this user attended (via rsvp going)
    rsvp(where: { user_id: { _eq: $userId }, status: { _eq: "going" } }) {
      id
      event { id event_date sessions { id completed } }
    }
    event_hosts(where: { profile_id: { _eq: $userId } }) {
      event { sessions { id } }
    }
  }
`;

const cardStyle = {
  background: '#fff',
  border: '1px solid rgba(60,20,30,.08)',
  borderRadius: 16,
  padding: 16,
  marginBottom: 12,
};

const sectionTitle = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: 1.5,
  textTransform: 'uppercase',
  color: 'var(--muted)',
  marginBottom: 10,
};

const divider = { borderTop: '1px solid rgba(60,20,30,.07)' };

function Stars({ rating, size = 13 }) {
  return (
    <span style={{ position: 'relative', display: 'inline-block', fontSize: size, letterSpacing: 2, lineHeight: 1 }}>
      <span style={{ color: 'rgba(60,20,30,.18)' }}>★★★★★</span>
      <span style={{ position: 'absolute', left: 0, top: 0, overflow: 'hidden', whiteSpace: 'nowrap', color: 'var(--gold)', width: `${rating / 5 * 100}%` }}>★★★★★</span>
    </span>
  );
}

function Toggle({ on, onChange, disabled }) {
  return (
    <div
      onClick={disabled ? undefined : onChange}
      style={{
        width: 44, height: 25, borderRadius: 13,
        cursor: disabled ? 'default' : 'pointer',
        background: on ? 'var(--wine)' : 'rgba(60,20,30,.15)',
        position: 'relative', transition: 'background 0.2s', flexShrink: 0,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <div style={{
        position: 'absolute', top: 3, left: on ? 22 : 3,
        width: 19, height: 19, borderRadius: '50%', background: 'white',
        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  );
}

// Nav SVG icons matching App.js
const NAV_TABS = [
  { id: 'karta', label: 'Karta', svg: <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/></svg> },
  { id: 'viner', label: 'Viner', svg: <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M8 22h8M12 11v11M6 2h12l-2 7a4 4 0 0 1-8 0Z"/></svg> },
  { id: 'druvor', label: 'Druvor', svg: <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="10" r="2.5"/><circle cx="15" cy="10" r="2.5"/><circle cx="12" cy="15" r="2.5"/><circle cx="9" cy="5" r="2"/><circle cx="15" cy="5" r="2"/><line x1="12" y1="2" x2="12" y2="3.5" strokeWidth="1.5"/></svg> },
  { id: 'provningar', label: 'Provningar', svg: <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> },
  { id: 'kalender', label: 'Kalender', svg: <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><circle cx="12" cy="16" r="1.5" fill="currentColor" stroke="none"/></svg> },
  { id: 'statistik', label: 'Statistik', svg: <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="12" width="4" height="9" rx="1"/><rect x="10" y="6" width="4" height="15" rx="1"/><rect x="17" y="2" width="4" height="19" rx="1"/></svg> },
];

export default function Profile({ profile, onClose, onNavigate, onWineClick, onGrapeClick }) {
  const [ratings, setRatings] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [hostedCount, setHostedCount] = useState(0);
  const [totalPoints, setTotalPoints] = useState(null);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [avatarUrl, setAvatarUrl] = useState(profile.avatar_url || null);
  const [calendarToken, setCalendarToken] = useState(profile.calendar_token || null);
  const [showCalInfo, setShowCalInfo] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [savingNotif, setSavingNotif] = useState(false);
  const push = usePushSubscription();
  const [phone, setPhone] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);
  const [phoneSaved, setPhoneSaved] = useState(false);
  const [userBadges, setUserBadges] = useState([]);
  const [activeTooltip, setActiveTooltip] = useState(null);

  function showBadgeTooltip(e, badge) {
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    setActiveTooltip({
      id: badge.id,
      x: Math.max(8, Math.min(centerX - 80, window.innerWidth - 168)),
      bottom: window.innerHeight - rect.top + 8,
    });
  }

  useEffect(() => {
    nhost.graphql.request(`query { profiles_by_pk(id: "${profile.id}") { avatar_url calendar_token email_notifications phone } }`)
      .then(({ data }) => {
        if (data?.profiles_by_pk?.avatar_url) setAvatarUrl(data.profiles_by_pk.avatar_url);
        if (data?.profiles_by_pk?.calendar_token) setCalendarToken(data.profiles_by_pk.calendar_token);
        if (data?.profiles_by_pk?.email_notifications !== undefined) setEmailNotifications(data.profiles_by_pk.email_notifications);
        if (data?.profiles_by_pk?.phone !== undefined) setPhone(data.profiles_by_pk.phone || '');
      });
  }, [profile.id]);

  useEffect(() => {
    async function loadBadges() {
      await checkBadges(profile.id);
      const { data } = await nhost.graphql.request(
        `query($uid: uuid!) { user_badges(where: { user_id: { _eq: $uid } }, order_by: { earned_at: asc }) { earned_at badge { id name description image_url } } }`,
        { uid: profile.id }
      );
      if (data) setUserBadges(data.user_badges);
    }
    loadBadges();
  }, [profile.id]);

  useEffect(() => {
    nhost.graphql.request(PROFILE_QUERY, { userId: profile.id }).then(({ data }) => {
      if (data) {
        setRatings(data.ratings);
        setRsvps(data.rsvp);
        setHostedCount(data.event_hosts?.filter(h => h.event?.sessions?.length > 0).length || 0);
        setPhone(data.profiles_by_pk?.phone || '');
      }
      setLoading(false);
    });
    nhost.graphql.request(`query($uid: uuid!) { user_points(where: { user_id: { _eq: $uid } }) { points } }`, { uid: profile.id })
      .then(({ data }) => {
        if (data) setTotalPoints(data.user_points.reduce((a, b) => a + b.points, 0));
      });
  }, [profile.id]);

  async function saveAvatar(url) {
    setAvatarUrl(url);
    await nhost.graphql.request(`
      mutation UpdateAvatar($id: uuid!, $url: String) {
        update_profiles_by_pk(pk_columns: { id: $id }, _set: { avatar_url: $url }) { id }
      }
    `, { id: profile.id, url });
    await awardPoints(profile.id, 'profile_avatar', profile.id);
  }

  async function savePhone() {
    setSavingPhone(true);
    await nhost.graphql.request(`
      mutation UpdatePhone($id: uuid!, $phone: String) {
        update_profiles_by_pk(pk_columns: { id: $id }, _set: { phone: $phone }) { id }
      }
    `, { id: profile.id, phone: phone.trim() || null });
    await awardPoints(profile.id, 'profile_phone', profile.id);
    setSavingPhone(false);
    setPhoneSaved(true);
    setTimeout(() => setPhoneSaved(false), 2000);
  }

  async function toggleEmailNotifications() {
    const newVal = !emailNotifications;
    setSavingNotif(true);
    setEmailNotifications(newVal);
    await nhost.graphql.request(`
      mutation UpdateNotifications($id: uuid!, $val: Boolean!) {
        update_profiles_by_pk(pk_columns: { id: $id }, _set: { email_notifications: $val }) { id }
      }
    `, { id: profile.id, val: newVal });
    setSavingNotif(false);
  }

  const totalRatings = ratings.length;
  const avgRating = totalRatings
    ? (ratings.reduce((a, b) => a + b.rating, 0) / totalRatings).toFixed(1)
    : '–';
  const sessionsAttended = rsvps.filter(r => r.event?.sessions?.some(s => s.completed)).length;

  const highRated = ratings.filter(r => r.rating >= 4 && r.wine?.region);
  const regionCount = {};
  highRated.forEach(r => { const reg = r.wine.region; regionCount[reg] = (regionCount[reg] || 0) + 1; });
  const favoriteRegion = Object.entries(regionCount).sort((a, b) => b[1] - a[1])[0]?.[0] || '–';

  const grapeRated = ratings.filter(r => r.rating >= 4 && r.wine?.grape);
  const grapeCount = {};
  grapeRated.forEach(r => { const g = r.wine.grape; grapeCount[g] = (grapeCount[g] || 0) + 1; });
  const favoriteGrape = Object.entries(grapeCount).sort((a, b) => b[1] - a[1])[0]?.[0] || '–';

  const topWines = [...ratings]
    .sort((a, b) => b.rating - a.rating || new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 5);

  const recentRatings = ratings.slice(0, 5);

  const memberSince = profile.created_at
    ? new Date(profile.created_at).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long' })
    : null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--cream)', zIndex: 100, overflowY: 'auto' }}>
      <div style={{ maxWidth: 480, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: 'var(--cream)', position: 'sticky', top: 0, zIndex: 10, borderBottom: '1px solid rgba(60,20,30,.06)' }}>
          <button
            onClick={onClose}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--wine)', fontSize: 14, fontWeight: 500, padding: '6px 0' }}
          >
            <ArrowLeft size={18} strokeWidth={1.8} />
            Tillbaka
          </button>
          <button
            onClick={() => nhost.auth.signOut()}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 14, fontWeight: 500, padding: '6px 0' }}
          >
            Logga ut
            <LogOut size={16} strokeWidth={1.8} />
          </button>
        </div>

        <div style={{ padding: '16px 16px 100px' }}>
          {/* Avatar card */}
          <div style={{ ...cardStyle, textAlign: 'center', paddingTop: 28, paddingBottom: 24, borderRadius: 20 }}>
            <ImageUpload
              currentUrl={avatarUrl}
              onUploaded={saveAvatar}
              size={84}
              shape="circle"
              label="Byt profilbild"
              showCamera
            />
            <div style={{ fontSize: 22, fontWeight: 600, fontFamily: 'var(--font-display)', color: 'var(--ink)', marginTop: 4 }}>{profile.display_name}</div>
            {memberSince && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>Medlem sedan {memberSince}</div>}
          </div>

          {/* Points card */}
          {totalPoints !== null && (
            <div style={{ ...cardStyle, background: 'var(--wine-light)', textAlign: 'center', borderRadius: 18, border: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 2 }}>
                <Award size={16} color="var(--wine)" strokeWidth={1.7} />
                <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--wine)' }}>Totala poäng</span>
              </div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 40, fontWeight: 600, color: 'var(--wine)', lineHeight: 1.1 }}>{totalPoints}p</div>
              <button
                onClick={() => setShowBreakdown(true)}
                style={{ marginTop: 10, fontSize: 13, color: 'var(--wine)', background: 'white', border: 'none', borderRadius: 20, padding: '7px 18px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 }}
              >
                Visa aktiviteter →
              </button>
            </div>
          )}
          {showBreakdown && (
            <PointsBreakdown
              userId={profile.id}
              displayName={profile.display_name}
              totalPoints={totalPoints}
              onClose={() => setShowBreakdown(false)}
            />
          )}

          {/* Inställningar */}
          <div style={cardStyle}>
            <div style={sectionTitle}>Inställningar</div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', marginBottom: 6 }}>Telefonnummer</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="t.ex. 070-123 45 67"
                  style={{ flex: 1, fontSize: 13, padding: '9px 12px', border: '1px solid rgba(139,42,62,.2)', borderRadius: 12, fontFamily: 'inherit', outline: 'none', background: '#fff' }}
                />
                <button
                  onClick={savePhone}
                  disabled={savingPhone}
                  style={{ flexShrink: 0, background: phoneSaved ? 'var(--teal-light)' : 'var(--wine-light)', color: phoneSaved ? 'var(--teal)' : 'var(--wine)', border: 'none', borderRadius: 12, padding: '9px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  {savingPhone ? '...' : phoneSaved ? '✓ Sparat' : 'Spara'}
                </button>
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Visas för andra medlemmar</div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 12, ...divider }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>E-postaviseringar</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>Få mail när ny provning planeras</div>
              </div>
              <Toggle on={emailNotifications} onChange={toggleEmailNotifications} disabled={savingNotif} />
            </div>

            {push.supported && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 12, marginTop: 12, ...divider }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>Push-notiser</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                    {push.permission === 'denied'
                      ? 'Blockerat i webbläsaren – ändra i inställningarna'
                      : 'Notiser direkt i mobilen'}
                  </div>
                </div>
                {push.permission !== 'denied' && (
                  <Toggle
                    on={push.subscribed}
                    onChange={push.subscribed ? push.unsubscribe : push.subscribe}
                    disabled={push.loading}
                  />
                )}
              </div>
            )}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)', fontSize: 13 }}>Laddar...</div>
          ) : (
            <>
              {/* Utmärkelser */}
              {userBadges.length > 0 && (
                <div style={cardStyle}>
                  <div style={sectionTitle}>Utmärkelser</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                    {userBadges.map(ub => (
                      <div
                        key={ub.badge.id}
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 68, textAlign: 'center', cursor: 'pointer' }}
                        onMouseEnter={e => showBadgeTooltip(e, ub.badge)}
                        onMouseLeave={() => setActiveTooltip(null)}
                        onClick={e => activeTooltip?.id === ub.badge.id ? setActiveTooltip(null) : showBadgeTooltip(e, ub.badge)}
                      >
                        <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--wine-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', border: '1.5px solid rgba(139,42,62,.15)', marginBottom: 6 }}>
                          {ub.badge.image_url
                            ? <img src={ub.badge.image_url} alt={ub.badge.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <Award size={22} color="var(--wine)" strokeWidth={1.5} />
                          }
                        </div>
                        <div style={{ fontSize: 10, fontWeight: 500, color: 'var(--ink)', lineHeight: 1.3 }}>{ub.badge.name}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {activeTooltip && (() => {
                const ub = userBadges.find(b => b.badge.id === activeTooltip.id);
                return ub?.badge.description ? (
                  <div style={{
                    position: 'fixed', left: activeTooltip.x, bottom: activeTooltip.bottom,
                    width: 160, background: 'white', border: '1px solid rgba(60,20,30,.08)',
                    borderRadius: 10, padding: '8px 10px', zIndex: 500,
                    boxShadow: '0 4px 16px rgba(60,20,30,.12)', pointerEvents: 'none',
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--wine)', marginBottom: 4 }}>{ub.badge.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.5 }}>{ub.badge.description}</div>
                  </div>
                ) : null;
              })()}

              {/* Statistik */}
              <div style={{ ...cardStyle, padding: '16px 16px 8px' }}>
                <div style={sectionTitle}>Statistik</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 8 }}>
                  {[
                    { num: totalRatings, label: 'Betygsatta viner' },
                    { num: avgRating, label: 'Snittbetyg' },
                    { num: hostedCount, label: 'Hållna provningar' },
                    { num: favoriteRegion !== '–' ? favoriteRegion.split(',')[0] : '–', label: 'Favoritregion' },
                  ].map(({ num, label }) => (
                    <div key={label} style={{ background: 'var(--cream)', borderRadius: 12, padding: '12px 14px', textAlign: 'center' }}>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, color: 'var(--wine)', lineHeight: 1 }}>{num}</div>
                      <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4, fontWeight: 500 }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Favoritdruva */}
              {favoriteGrape !== '–' && (
                <div style={{ ...cardStyle, cursor: 'pointer' }} onClick={() => onGrapeClick?.(favoriteGrape)}>
                  <div style={sectionTitle}>Favoritdruva</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--wine-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Wine size={18} color="var(--wine)" strokeWidth={1.7} />
                    </div>
                    <div>
                      <div style={{ fontSize: 17, fontWeight: 600, fontFamily: 'var(--font-display)', color: 'var(--wine)' }}>{favoriteGrape}</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 1 }}>Baserat på dina högt betygsatta viner</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Topp 5 viner */}
              {topWines.length > 0 && (
                <div style={cardStyle}>
                  <div style={sectionTitle}>Dina topp 5 viner</div>
                  {topWines.map((r, i) => (
                    <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', ...(i < topWines.length - 1 ? divider : {}), cursor: 'pointer' }} onClick={() => onWineClick?.(r.wine)}>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 300, color: 'var(--muted)', width: 22, textAlign: 'center', flexShrink: 0 }}>{i + 1}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>{r.wine?.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>
                          {r.wine?.vintage ? `${r.wine.vintage} · ` : ''}{r.wine?.region}
                        </div>
                      </div>
                      <Stars rating={r.rating} size={13} />
                    </div>
                  ))}
                </div>
              )}

              {/* Senaste betyg */}
              {recentRatings.length > 0 && (
                <div style={cardStyle}>
                  <div style={sectionTitle}>Senaste betyg</div>
                  {recentRatings.map((r, i) => (
                    <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', ...(i < recentRatings.length - 1 ? divider : {}), cursor: 'pointer' }} onClick={() => onWineClick?.(r.wine)}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>{r.wine?.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>
                          {r.wine?.session?.event?.event_date ? sweDate(r.wine.session.event.event_date) : 'Löst vin'}
                          {r.wine?.session?.profile?.display_name ? ` · Provning av ${r.wine.session.profile.display_name}` : ''}
                        </div>
                        {r.comment && <div style={{ fontSize: 11, color: 'var(--muted)', fontStyle: 'italic', fontFamily: 'var(--font-display)', marginTop: 2 }}>"{r.comment}"</div>}
                      </div>
                      <Stars rating={r.rating} size={13} />
                    </div>
                  ))}
                </div>
              )}

              {totalRatings === 0 && (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--muted)' }}>
                  <Wine size={32} color="rgba(60,20,30,.2)" strokeWidth={1.5} style={{ marginBottom: 10 }} />
                  <div style={{ fontSize: 14 }}>Du har inte betygsatt några viner än.</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Bottom nav */}
      <nav className="bottom-nav">
        {NAV_TABS.map(t => (
          <button key={t.id} className="nav-btn" onClick={() => { onClose(); onNavigate(t.id); }}>
            <span className="nav-icon">{t.svg}</span>
            {t.label}
          </button>
        ))}
      </nav>
    </div>
  );
}
